/**
 * HUD regions: where chrome is allowed to be.
 *
 * Before this file, six overlays positioned themselves independently against
 * the viewport — five `position: fixed` blocks in `main.ts`, the HUD corners and
 * the toast stack in the stylesheet — each with its own offsets. Nothing
 * reserved a region, so nothing could be placed without first reading every
 * other overlay, and the control-hint line took three positions to find one that
 * did not collide (#52). That is the tell for a missing layout, not a missing
 * nudge.
 *
 * The scheme is one CSS grid covering the viewport, with named areas. An overlay
 * claims an area; the grid places it. **Two distinct grid areas cannot
 * intersect** — that is a property of the layout algorithm, not a promise this
 * file makes — so a collision between overlays in different regions stops being
 * something you catch in a screenshot and becomes something you cannot express.
 * Overlays that share a region are flow siblings in a flex column, which cannot
 * overlap either. What is left is a single failure mode, content that outgrows
 * its region, and `.reg` clips rather than spills so that failure stays inside
 * the contract.
 *
 * Adding an overlay is now: pick a region, add it to `OVERLAY_REGION`, call
 * `claimRegion`. No offsets to choose, and nothing to re-check.
 *
 * `overlappingPairs` lives here rather than in a test because it is the
 * measurement #55 asks for and it needs to be runnable against a live document
 * (see `measureOverlaps`), not only against fixtures.
 *
 * This module imports nothing. `style.ts` interpolates `REGION_CSS` into the
 * sheet, so the edge has to run that way round, and the two-line `el` helper is
 * inlined below rather than imported back — a cycle between the stylesheet and
 * the layout that generates part of it is the kind of thing that works until
 * someone reorders an import.
 */

// ------------------------------------------------------------------ regions

export type RegionName =
  | 'top-left'
  | 'top-band'
  | 'top-right'
  | 'left-rail'
  | 'centre-prompt'
  | 'bottom-band';

export const REGION_NAMES: readonly RegionName[] = [
  'top-left',
  'top-band',
  'top-right',
  'left-rail',
  'centre-prompt',
  'bottom-band',
];

/**
 * The grid, cell by cell — three columns, four rows. `null` is deliberately
 * unclaimed space: the middle of the screen is the picture (docs/01 §6.4) and
 * the layout is what keeps it empty.
 *
 * `REGION_CSS` is generated from this array rather than written beside it, so
 * the template the browser reads and the template the test reasons about are
 * the same data. The test asserts each name forms a rectangle — which CSS grid
 * requires anyway, and which a hand-written template can violate silently by
 * placing nothing at all.
 */
export const REGION_GRID: readonly (readonly (RegionName | null)[])[] = [
  ['top-left', 'top-band', 'top-right'],
  ['left-rail', null, null],
  ['centre-prompt', 'centre-prompt', 'centre-prompt'],
  ['bottom-band', 'bottom-band', 'bottom-band'],
];

/**
 * Every piece of chrome that shares the screen with the running game, and the
 * region it claims.
 *
 * This is the whole layout decision, in one table. Note what it makes visible
 * that a pile of offsets could not: the run log and the toast stack claim the
 * *same* region, which is the point — they were two renderings of one feed in
 * two corners of the screen, and putting them in one column is what makes that
 * duplication a thing you see rather than a thing you discover.
 */
export const OVERLAY_REGION = {
  /** Vitals, shield, statuses, purse. The first thing you look at. */
  'hud.vitals': 'top-left',
  /** Control hints. Reference text, read once, centred out of everyone's way. */
  'run.hints': 'top-band',
  /** Toasts — the loud tier of the run feed. */
  'hud.toasts': 'top-right',
  /** The run log — the quiet tier of the same feed, directly beneath it. */
  'run.log': 'top-right',
  /** F3 debug readout. Long, wide, and nobody else wants the left flank. */
  'run.debug': 'left-rail',
  /** Weapon altar prompt. */
  'run.altar': 'centre-prompt',
  /** Ability-cache prompt. */
  'run.cache': 'centre-prompt',
  /** Vendor stall — the same in-world prompt shape as the two above (#11). */
  'hud.vendor': 'centre-prompt',
  /** Abilities, dash pips, both hand cards, rhythm meter. */
  'hud.bottom': 'bottom-band',
} as const satisfies Record<string, RegionName>;

export type OverlayId = keyof typeof OVERLAY_REGION;

// -------------------------------------------------------------------- css

function gridTemplateAreas(): string {
  return REGION_GRID.map((row) => `    "${row.map((n) => n ?? '.').join(' ')}"`).join('\n');
}

/**
 * Injected by `style.ts` as part of `UI_CSS`.
 *
 * Three equal columns rather than fixed side rails: an equal split needs no
 * magic number, and the two corner regions are the ones that grow with content
 * (a six-deep toast stack, a nine-chip ability row), so giving them a third each
 * is the placement least likely to need revisiting.
 *
 * The top row is capped at 46vh. Without it a long enough feed grows row 1 past
 * the viewport and pushes the bottom band off the bottom, which would be the
 * same class of defect one level up.
 */
export const REGION_CSS = /* css */ `
.ui-regions {
  position: absolute; inset: 0;
  display: grid;
  padding: 12px;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
  /* auto | rest | auto | auto — the middle row is the picture. */
  grid-template-rows: auto minmax(0, 1fr) auto auto;
  grid-template-areas:
${gridTemplateAreas()};
  overflow: hidden;
}
/* Overlays sharing a region are flow siblings, so they cannot overlap either.
   overflow:hidden is the backstop for the one failure a grid cannot prevent:
   content taller than its own region. Clipped is debuggable; spilled is #52. */
.reg {
  display: flex; flex-direction: column; gap: 6px;
  min-width: 0; min-height: 0; overflow: hidden;
}
/* An overlay may not be larger than the region it claimed.

   overflow:hidden above stops an oversized overlay being *seen* outside its
   region; it does not stop it *being* outside. An unconstrained flex item keeps
   its content size, and getBoundingClientRect reports that box — which is what
   measureOverlaps reads. So a clipped overflow still measured as a collision:
   the vitals block 31px wider than the top-left region, the run log 79px below
   the top band. A check that reports a collision the player cannot see is a
   check people stop reading.

   Interface scale (#69) is what made this reachable — at Huge on a 540px-wide
   window the vitals line is wider than a third of the screen and four toasts
   are taller than the 46vh cap. max-width:100% binds the cross axis to the
   region. min-height:0 turns off the automatic minimum size that otherwise
   refuses to let a flex item shrink below its content, so the column fits the
   cap instead of hanging out of the bottom of it.

   overflow:hidden here rather than only on .reg is the half a screenshot
   caught and the measurement did not. measureOverlaps reads the region and its
   direct children, so a squeezed .toasts box measured as fitting — while its
   own children kept their content size and printed straight through the run
   log underneath. Clipping the shrunken child is what makes the box it now
   reports the box it actually occupies. */
.reg > * { max-width: 100%; min-width: 0; min-height: 0; overflow: hidden; }
.reg--top-left      { grid-area: top-left;      align-items: flex-start; justify-content: flex-start; max-height: 46vh; }
.reg--top-band      { grid-area: top-band;      align-items: center;     justify-content: flex-start; max-height: 46vh; }
.reg--top-right     { grid-area: top-right;     align-items: flex-end;   justify-content: flex-start; max-height: 46vh; }
.reg--left-rail     { grid-area: left-rail;     align-items: flex-start; justify-content: flex-start; }
.reg--centre-prompt { grid-area: centre-prompt; align-items: center;     justify-content: flex-end; }
.reg--bottom-band   { grid-area: bottom-band;   justify-content: flex-end; }
`;

// -------------------------------------------------------------------- dom

const LAYER_CLASS = 'ui-regions';

function div(cls: string): HTMLDivElement {
  const e = document.createElement('div');
  e.className = cls;
  return e;
}

/**
 * The one region layer for this UI root, created on first claim.
 *
 * Resolution walks up to `.ui-root` so that the HUD (whose host is the hud
 * layer, several levels down) and `main.ts` (whose host *is* the root) land on
 * the same layer. Two layers would reintroduce exactly the problem this file
 * removes.
 *
 * Inserted as the **first** child, so the modal layer still paints over it.
 * That is a change for the three overlays `main.ts` used to append last: the
 * control hints printed over the pause menu, which nobody had noticed because
 * you have to open the pause menu to see it.
 */
export function regionLayer(host: HTMLElement): HTMLElement {
  const root = (host.closest('.ui-root') as HTMLElement | null) ?? host;
  const found = root.querySelector(`:scope > .${LAYER_CLASS}`) as HTMLElement | null;
  if (found) return found;
  const layer = div(LAYER_CLASS);
  // index.html forces `#ui-root > * { pointer-events: auto }`; inline is the
  // only thing that reliably beats an id selector without an !important war.
  layer.style.pointerEvents = 'none';
  root.insertBefore(layer, root.firstChild);
  return layer;
}

/** The container for `name`, created on first claim. Idempotent. */
export function claimRegion(host: HTMLElement, name: RegionName): HTMLElement {
  const layer = regionLayer(host);
  const found = layer.querySelector(`:scope > [data-region="${name}"]`) as HTMLElement | null;
  if (found) return found;
  const region = div(`reg reg--${name}`);
  region.dataset.region = name;
  layer.appendChild(region);
  return region;
}

/** Drop the whole layer. Called from `UIRoot.destroy`. */
export function removeRegionLayer(host: HTMLElement): void {
  const root = (host.closest('.ui-root') as HTMLElement | null) ?? host;
  root.querySelector(`:scope > .${LAYER_CLASS}`)?.remove();
}

// --------------------------------------------------------------- overlaps

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Box {
  id: string;
  rect: Rect;
}

export interface OverlapPair {
  a: string;
  b: string;
  ox: number;
  oy: number;
}

/**
 * Every pair of boxes whose rectangles share area, with how much.
 *
 * Exact, deterministic, and the single most-reported visual defect in the
 * project reduces to it (#55). Two deliberate exclusions:
 *
 *   - **A zero-area box collides with nothing.** A hidden overlay measures 0x0
 *     rather than leaving the tree, and a collision reported against something
 *     nobody can see is the kind of false alarm that teaches people to stop
 *     reading the output.
 *   - **Touching is not overlapping.** Two boxes sharing an edge is the normal
 *     result of a flex gap of zero, not a defect.
 *
 * Nesting is *not* excluded here — a caller measuring a live document has to
 * drop ancestor/descendant pairs itself, because "the container contains its
 * own child" is true of every well-formed tree and is not this function's
 * business to know about. `measureOverlaps` does it.
 */
export function overlappingPairs(boxes: readonly Box[]): OverlapPair[] {
  const live = boxes.filter((b) => b.rect.w > 0 && b.rect.h > 0);
  const out: OverlapPair[] = [];
  for (let i = 0; i < live.length; i++) {
    for (let j = i + 1; j < live.length; j++) {
      const a = live[i].rect;
      const b = live[j].rect;
      const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
      const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
      if (ox > 0 && oy > 0) out.push({ a: live[i].id, b: live[j].id, ox, oy });
    }
  }
  return out;
}

/**
 * Run the check against a live document.
 *
 * This is the half of #55 that needs a real layout engine and therefore cannot
 * gate `npm run verify` as the suite is configured — vitest runs in `node`, and
 * both DOM shims that would give it a `document` implement no layout at all, so
 * `getBoundingClientRect()` returns zeros and a rect test on top of one is a
 * test that can never fail. Rather than ship that, the arithmetic is tested
 * directly (`tests/hudLayout.test.ts`) and this runs in the browser:
 *
 *     await import('/src/ui/hudLayout.ts').then(m => m.measureOverlaps())
 *
 * Making it gate a build means putting a headless browser in the suite; the
 * tests file records that trade.
 */
export function measureOverlaps(root: HTMLElement = document.body): {
  viewport: [number, number];
  boxes: Box[];
  pairs: OverlapPair[];
} {
  const found: Array<{ id: string; el: Element }> = [];
  root.querySelectorAll('[data-region]').forEach((e) => {
    const name = (e as HTMLElement).dataset.region ?? '?';
    found.push({ id: `region:${name}`, el: e });
    // Direct children of a region are the overlays themselves; measuring them
    // as well is what catches an overlay that outgrew its region.
    Array.from(e.children).forEach((c, i) => found.push({ id: `${name}[${i}]`, el: c }));
  });

  const live: Array<{ id: string; el: Element; rect: Rect }> = [];
  for (const { id, el: e } of found) {
    const cs = getComputedStyle(e);
    if (cs.display === 'none' || cs.visibility === 'hidden') continue;
    const r = e.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    live.push({
      id,
      el: e,
      rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
    });
  }

  const pairs: OverlapPair[] = [];
  for (let i = 0; i < live.length; i++) {
    for (let j = i + 1; j < live.length; j++) {
      if (live[i].el.contains(live[j].el) || live[j].el.contains(live[i].el)) continue;
      const [p] = overlappingPairs([
        { id: live[i].id, rect: live[i].rect },
        { id: live[j].id, rect: live[j].rect },
      ]);
      if (p) pairs.push(p);
    }
  }

  return {
    viewport: [window.innerWidth, window.innerHeight],
    boxes: live.map(({ id, rect }) => ({ id, rect })),
    pairs,
  };
}
